import { AbVariantFormValue, ExperimentPayloadConfig, SurveyPayloadValue } from "../../store/types"
import { EXPERIMENT_PAYLOAD_CONFIGS } from "./experiment-configs"

export const getDiscriminatorFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): string => {
  if (!payload || typeof payload !== "object") return Object.keys(config.values ?? {})[0] ?? ""

  return (payload as any)?.value?.[config.discriminator!] ?? Object.keys(config.values ?? {})[0] ?? ""
}

export const getParamsFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): Record<string, number | string> | undefined => {
  if (!payload || typeof payload !== "object" || !config.paramsKey) return undefined

  return (payload as any)?.value?.[config.paramsKey] ?? undefined
}

export const getOrderedListFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): string[] | undefined => {
  if (!payload || typeof payload !== "object" || !config.orderedList) return undefined

  return (payload as any)?.value?.[config.orderedList.key] ?? config.orderedList.defaultValue
}

export const getBooleanFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): boolean | undefined => {
  if (!payload || typeof payload !== "object" || !config.booleanField) return undefined

  return (payload as any)?.value?.[config.booleanField.key] ?? config.booleanField.defaultValue
}

export const getMultiSelectFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): string[] | undefined => {
  if (!payload || typeof payload !== "object" || !config.multiSelect) return undefined

  const raw = (payload as any)?.value?.[config.multiSelect.key]
  const validOptions = new Set(config.multiSelect.options.map(o => o.value))

  // Defensive: backend may have stale option keys (after we add/remove
  // options here) — drop unknowns so the UI doesn't render a broken tag.
  if (!Array.isArray(raw)) return config.multiSelect.defaultValue

  return raw.filter((v: unknown): v is string => typeof v === "string" && validOptions.has(v))
}

export const getSurveyFromPayload = (
  payload: unknown,
  config: ExperimentPayloadConfig,
): SurveyPayloadValue | undefined => {
  if (!payload || typeof payload !== "object" || !config.surveyQuestions) return undefined

  const value = (payload as any)?.value
  // Defensive: backend may return `questions` as undefined, an object, or
  // some other type if a malformed variant was persisted. SurveyQuestionsEditor
  // calls `.map`/spread on this array unconditionally, so coerce to `[]` and
  // filter out anything that doesn't match the { key, isRequired } shape
  // before handing it to the UI.
  const rawQuestions = Array.isArray(value?.questions) ? value.questions : []
  const questions = rawQuestions.filter((q: unknown): q is { key: string; isRequired: boolean } => {
    if (typeof q !== "object" || q === null) return false

    const obj = q as { key?: unknown; isRequired?: unknown }

    return typeof obj.key === "string" && typeof obj.isRequired === "boolean"
  })

  return {
    enabled: value?.enabled === true,
    questions,
  }
}

export const buildPayload = (
  experimentName: string,
  v: AbVariantFormValue,
): Record<string, unknown> | undefined => {
  const config = EXPERIMENT_PAYLOAD_CONFIGS[experimentName]

  if (!config) return undefined

  if (config.surveyQuestions) {
    const survey = v.payloadSurvey ?? { enabled: false, questions: [] }

    return { version: 0, value: { enabled: survey.enabled, questions: survey.questions } }
  }

  if (config.multiSelect) {
    const selected = v.payloadMultiSelect ?? config.multiSelect.defaultValue

    return { version: 0, value: { [config.multiSelect.key]: selected } }
  }

  if (config.booleanField) {
    const enabled = v.payloadBoolean ?? config.booleanField.defaultValue

    return { version: 0, value: { [config.booleanField.key]: enabled } }
  }

  if (config.orderedList) {
    const sections = v.payloadOrderedList ?? config.orderedList.defaultValue

    return { version: 0, value: { [config.orderedList.key]: sections } }
  }

  if (!v.payloadDiscriminator) return undefined

  const value: Record<string, unknown> = { [config.discriminator!]: v.payloadDiscriminator }
  const valueConfig = config.values?.[v.payloadDiscriminator]

  if (config.paramsKey && valueConfig?.params && v.payloadParams) {
    const params: Record<string, number | string> = {}

    for (const { key } of valueConfig.params) {
      const val = v.payloadParams[key]

      if (val !== undefined && val !== null) params[key] = val
    }

    if (Object.keys(params).length > 0) value[config.paramsKey] = params
  }

  return { version: 0, value }
}
